import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Seo from '../components/Seo';
import { Phone } from 'lucide-react';
import { Clock } from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import { HeartPulse } from 'lucide-react';
import { CheckCircle2 } from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import { Hospital } from 'lucide-react';
import { Users } from 'lucide-react';
import { Stethoscope } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { Timer } from 'lucide-react';
import { FileCheck } from 'lucide-react';

const META_TITLE = 'Hospital Discharge Transportation Richmond TX & Greater Houston | MoveraCare';
const META_DESCRIPTION = 'Same-day and scheduled hospital discharge non-emergency medical transportation serving Richmond TX, Fort Bend County, Harris County, Sugar Land, Missouri City, Katy, and Greater Houston. Wheelchair and ambulatory options. Call 832-925-2128.';
const META_PATH = '/services/hospital-discharge';

const SCHEMA_PROVIDER = {
  '@type': 'MedicalBusiness',
  name: 'MoveraCare Mobility',
  legalName: 'MoveraCare Services LLC',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  faxNumber: '+1-713-583-2334',
  url: 'https://www.moveracaremobility.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  areaServed: [
    { '@type': 'City', name: 'Richmond, TX' },
    { '@type': 'City', name: 'Sugar Land, TX' },
    { '@type': 'City', name: 'Missouri City, TX' },
    { '@type': 'City', name: 'Katy, TX' },
    { '@type': 'City', name: 'Rosenberg, TX' },
    { '@type': 'City', name: 'Stafford, TX' },
    { '@type': 'City', name: 'Fulshear, TX' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
    { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
  ],
};

const SCHEMA_MAIN = {
  '@context': 'https://schema.org',
  '@type': 'MedicalProcedure',
  name: 'Hospital Discharge Medical Transportation',
  description: 'Same-day and scheduled hospital discharge transportation in Richmond, TX and across Fort Bend County, Harris County, and Greater Houston. Non-emergency medical transportation from hospitals to home, skilled nursing, rehabilitation, or assisted living for medically stable patients.',
  bodyLocation: 'Richmond, Texas',
  followup: 'Wheelchair or ambulatory transportation with door-to-door or curb-to-curb assistance coordinated at booking.',
  preparation: 'Coordinate discharge time and pickup details with hospital case managers.',
  provider: SCHEMA_PROVIDER,
};

const SCHEMA_FAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can you handle same-day hospital discharges in Richmond TX?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Same-day discharge transportation is a core service; dispatch confirms the pickup window when the trip is accepted. Book at (832) 925-2128.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are your drivers trained for hospital discharge transport?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'MoveraCare drivers assist passengers during transport. Please share the mobility level and any equipment needs (including oxygen) when booking so dispatch can assess whether we can safely serve the trip.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of patients do you transport from hospitals?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We transport ambulatory and wheelchair passengers who are medically stable. We do not transport emergency or unstable patients — call 911 for those.',
      },
    },
    {
      '@type': 'Question',
      name: 'How is payment handled for hospital discharge transportation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Payment arrangements and any required authorization are confirmed before the trip. Private-pay and authorized partner trips may be available. Contact dispatch to confirm the payment method for your ride.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you transport to skilled nursing or rehabilitation facilities?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We transport medically stable patients from hospitals to SNFs, rehab centers, assisted living, or home. Door-to-door or curb-to-curb assistance is coordinated at booking.',
      },
    },
  ],
};

function HospitalDischarge() {
  return (
    <div className="min-h-screen pt-20" data-testid="hospital-discharge-page">
      <Seo
        title={META_TITLE}
        description={META_DESCRIPTION}
        path={META_PATH}
        schema={[SCHEMA_MAIN, SCHEMA_FAQ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Hospital Discharge', url: META_PATH },
        ]}
      />
      {/* Hero */}
      <section className="bg-[#004C97] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6" data-testid="badge-same-day">
              <AlertTriangle className="w-4 h-4" />
              <span>SAME-DAY DISCHARGE • RICHMOND, TX</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Hospital Discharge Medical Transport in Richmond, TX
            </h1>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed">
              When the hospital is ready and the patient is too, <span className="text-[#F9A825] font-semibold">we&apos;re ready to coordinate.</span> Same-day and scheduled discharge transportation to home, SNF, rehab, or assisted living for medically stable patients, with wheelchair or ambulatory options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <a href="tel:+18329252128">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="cta-call-dispatch">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch — (832) 925-2128
                </Button>
              </a>
              <Link to="/book">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-8" data-testid="cta-book-online">
                  Book Online
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <p className="text-sm text-gray-300 flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Responsive discharge dispatch during operating hours
            </p>
          </div>
        </div>
      </section>

      {/* Trust / Why Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 text-center">
            Built for the Pace of Hospital Discharge
          </h2>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12">
            Discharge planning moves fast. Our team is structured to match it — without compromising patient safety or dignity.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-2 hover:border-[#F9A825] transition-all" data-testid="trust-same-day-discharge-ready">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#F9A825]/10 flex items-center justify-center mb-4">
                  <Timer className="w-6 h-6 text-[#F9A825]" />
                </div>
                <h3 className="text-lg font-semibold text-[#004C97] mb-2">Same-Day Discharge Ready</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Same-day discharge trips are coordinated when capacity allows. Dispatch confirms the pickup window when we accept the trip.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#F9A825] transition-all" data-testid="trust-driver-assistance">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#F9A825]/10 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-[#F9A825]" />
                </div>
                <h3 className="text-lg font-semibold text-[#004C97] mb-2">Driver Assistance During Transport</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Drivers assist passengers during transport. Please share the mobility level and any equipment needs (including oxygen) when booking so dispatch can assess the trip.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#F9A825] transition-all" data-testid="trust-post-hospital-care">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#F9A825]/10 flex items-center justify-center mb-4">
                  <HeartPulse className="w-6 h-6 text-[#F9A825]" />
                </div>
                <h3 className="text-lg font-semibold text-[#004C97] mb-2">Post-Hospital Handoff</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Door-to-door or curb-to-curb assistance is coordinated at booking. Drivers assist with paperwork and a safe handoff to family or facility staff.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:border-[#F9A825] transition-all" data-testid="trust-payment-coordination">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-[#F9A825]/10 flex items-center justify-center mb-4">
                  <FileCheck className="w-6 h-6 text-[#F9A825]" />
                </div>
                <h3 className="text-lg font-semibold text-[#004C97] mb-2">Payment & Authorization</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Payment arrangements and any required authorization are confirmed before the trip. Contact dispatch to confirm the payment method for your ride.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-12 text-center">
            How a Discharge Ride Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#F9A825] transition-all" data-testid="process-step-1">
              <div className="w-12 h-12 rounded-full bg-[#004C97] text-white flex items-center justify-center font-bold text-xl mb-4">1</div>
              <h3 className="text-lg font-semibold text-[#004C97] mb-2">Discharge Coordinator Calls Dispatch</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Case manager or family calls (832) 925-2128 with patient name, mobility level, and target discharge time.
              </p>
            </div>
            <div className="relative bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#F9A825] transition-all" data-testid="process-step-2">
              <div className="w-12 h-12 rounded-full bg-[#004C97] text-white flex items-center justify-center font-bold text-xl mb-4">2</div>
              <h3 className="text-lg font-semibold text-[#004C97] mb-2">We Confirm the Trip</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Vehicle assigned (wheelchair van or ambulatory) and pickup window confirmed via call/text when dispatch accepts the trip. Payment method is confirmed at this step.
              </p>
            </div>
            <div className="relative bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#F9A825] transition-all" data-testid="process-step-3">
              <div className="w-12 h-12 rounded-full bg-[#004C97] text-white flex items-center justify-center font-bold text-xl mb-4">3</div>
              <h3 className="text-lg font-semibold text-[#004C97] mb-2">Driver Arrives</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Driver checks in at nursing station, assists with discharge paperwork, and supports a safe transfer to the vehicle.
              </p>
            </div>
            <div className="relative bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-[#F9A825] transition-all" data-testid="process-step-4">
              <div className="w-12 h-12 rounded-full bg-[#004C97] text-white flex items-center justify-center font-bold text-xl mb-4">4</div>
              <h3 className="text-lg font-semibold text-[#004C97] mb-2">Delivery to Next Site of Care</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Patient transported to home, SNF, rehab, or assisted living. Door-to-door or curb-to-curb assistance is coordinated at booking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Hospitals We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6">
                Hospitals We Discharge From
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We have established discharge workflows with major hospitals across Richmond, Sugar Land, Katy, and Missouri City — meaning faster handoffs, fewer delays, and case managers who already know our drivers.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">OakBend Medical Center</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Houston Methodist Sugar Land Hospital</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Memorial Hermann Sugar Land</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Memorial Hermann Katy Hospital</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">HCA Houston Healthcare West</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#F9A825] mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">Texas Children&apos;s Hospital West Campus</span>
                </li>
              </ul>
            </div>
            <Card className="bg-[#004C97] border-2 border-[#F9A825] text-white">
              <CardContent className="p-8">
                <Hospital className="w-12 h-12 text-[#F9A825] mb-4" />
                <h3 className="text-2xl font-bold mb-3">Are You a Discharge Planner?</h3>
                <p className="text-gray-100 mb-6 leading-relaxed">
                  Set up a facility account for streamlined billing coordination and a single point of contact for scheduled discharges.
                </p>
                <Link to="/partners">
                  <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold w-full" data-testid="cta-facility-partner">
                    Become a Healthcare Partner
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Patient Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-10 text-center">
            Who We Transport on Discharge
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-2">
              <CardHeader>
                <Users className="w-10 h-10 text-[#F9A825] mb-2" />
                <CardTitle className="text-[#004C97]">Ambulatory Patients</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Sedan or van transport for patients who can walk with light assistance.</p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <Stethoscope className="w-10 h-10 text-[#F9A825] mb-2" />
                <CardTitle className="text-[#004C97]">Wheelchair Patients</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-10 text-center">
            Hospital Discharge FAQ
          </h2>
          <div className="space-y-4" data-testid="hospital-discharge-faq">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#004C97]">Can you handle same-day hospital discharges in Richmond TX?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Yes. Same-day discharge transportation is a core service; dispatch confirms the pickup window when the trip is accepted. Book at (832) 925-2128.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#004C97]">Are your drivers trained for hospital discharge transport?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  MoveraCare drivers assist passengers during transport. Please share the mobility level and any equipment needs (including oxygen) when booking so dispatch can assess whether we can safely serve the trip.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#004C97]">What types of patients do you transport from hospitals?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We transport ambulatory and wheelchair passengers who are medically stable. We do not transport emergency or unstable patients — call 911 for those.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#004C97]">How is payment handled for hospital discharge transportation?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Payment arrangements and any required authorization are confirmed before the trip. Private-pay and authorized partner trips may be available. Contact dispatch to confirm the payment method for your ride.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#004C97]">Do you transport to skilled nursing or rehabilitation facilities?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Yes. We transport medically stable patients from hospitals to SNFs, rehab centers, assisted living, or home. Door-to-door or curb-to-curb assistance is coordinated at booking.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Related services — contextual in-content links that help
              readers plan the rest of the trip (wheelchair van, dialysis
              follow-ups after discharge, recurring rehab rides). */}
          <div className="mt-12 pt-8 border-t border-gray-200 max-w-4xl mx-auto" data-testid="discharge-related-services">
            <h3 className="text-xl font-bold text-[#004C97] mb-3">Related transportation services</h3>
            <p className="text-gray-700 leading-relaxed">
              Many patients being discharged also need a{' '}
              <Link
                to="/services/wheelchair-transportation"
                className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                data-testid="discharge-link-wheelchair"
              >
                wheelchair-accessible vehicle
              </Link>{' '}
              with proper securement, especially when going home from an inpatient stay. Discharges to rehab centers and skilled nursing facilities often continue with{' '}
              <Link
                to="/services/therapy-rehabilitation-transportation"
                className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                data-testid="discharge-link-therapy"
              >
                therapy and rehabilitation transportation
              </Link>, and dialysis patients returning to a treatment schedule can book{' '}
              <Link
                to="/services/dialysis-transportation"
                className="text-[#004C97] font-semibold underline hover:text-[#F9A825]"
                data-testid="discharge-link-dialysis"
              >
                dialysis transportation
              </Link>{' '}
              on standing weekly cycles.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#004C97]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Discharge Today? Call Now.
          </h2>
          <p className="text-xl text-gray-100 mb-8">
            Same-day and scheduled discharge transportation for medically stable patients across Richmond, TX and surrounding Fort Bend County. Dispatch confirms the pickup window when we accept the trip.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+18329252128">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-10 shadow-lg" data-testid="cta-final-call">
                <Phone className="mr-2 w-5 h-5" />
                Call (832) 925-2128
              </Button>
            </a>
            <Link to="/book">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold text-lg px-10" data-testid="cta-final-book">
                Book Discharge Ride
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HospitalDischarge;
